import { RequestCategory, UserRole } from '@kfolks/types'
import { z } from 'zod'

import { workflowConfigSchema } from './workflow.schema.js'

/**
 * Zod schemas for request-type CRUD (Spec 1 of Request Center).
 *
 * Deep form-schema validation (duplicate field IDs, dangling data sources,
 * conditional references) lives in the shared `validateFormSchema` helper
 * (Task Group 7) — not in this Zod schema. Zod only shape-checks that
 * `formSchema` is an object; the service layer calls `validateFormSchema`
 * on write. Same validator is consumed by Spec 5's visual builder.
 *
 * `workflowConfig` is optional on update (and not part of create — newly
 * created types start with `{}` and are filled in via the workflow builder
 * after creation). The wire-shape Zod check is `workflowConfigSchema`; the
 * service layer additionally runs `validateWorkflowConfig` for cross-step
 * graph invariants (reachability, dangling targets, terminal-reachability).
 */

/**
 * Spec 2 lifts the Spec 1 narrowing — `viewerFromEvent()` now populates
 * `viewer.departments` and `viewer.entraGroups` via an `EmployeeAdapter`
 * lookup, so all three dimensions are honoured end-to-end by
 * `RequestTypeService.evaluateVisibility`. Authoring may freely populate
 * any combination of `roles` / `departments` / `entraGroups`.
 */
const visibilityRulesSchema = z
	.object({
		roles: z.array(z.enum(UserRole)).optional(),
		departments: z.array(z.string()).optional(),
		entraGroups: z.array(z.string()).optional(),
	})
	.optional()

export const requestTypeCreateSchema = z.object({
	slug: z.string().min(1).max(100),
	name: z.string().min(1).max(255),
	description: z.string().optional(),
	category: z.enum(RequestCategory),
	icon: z.string().max(50).optional(),
	color: z.string().max(7).optional(),
	formSchema: z.record(z.string(), z.unknown()),
	/**
	 * Optional one-line `{{field_id}}` template. Shape-only check here —
	 * referential validation (every `{{token}}` must exist in
	 * `formSchema.fields[].id`) lives in `validateSummaryTemplate` and runs
	 * at the service layer, paired with `validateFormSchema`.
	 */
	summaryTemplate: z.string().max(500).nullish(),
	visibilityRules: visibilityRulesSchema,
	sortOrder: z.number().int().optional(),
	/** Optional admin-supplied note saved to `request_type_versions.change_note`. */
	changeNote: z.string().max(500).nullish(),
})

/**
 * Update accepts everything `create` accepts (all optional via `.partial()`)
 * plus an optional `workflowConfig`, validated against `workflowConfigSchema`.
 * The visual workflow builder saves via this endpoint; cross-step graph
 * invariants are enforced at the service layer via `validateWorkflowConfig`.
 */
export const requestTypeUpdateSchema = requestTypeCreateSchema.partial().extend({
	workflowConfig: workflowConfigSchema.optional(),
	/** Optional admin-supplied note saved to `request_type_versions.change_note`. */
	changeNote: z.string().max(500).nullish(),
})

export type RequestTypeCreateInput = z.infer<typeof requestTypeCreateSchema>
export type RequestTypeUpdateInput = z.infer<typeof requestTypeUpdateSchema>
